import SuggestionPanelComponent, { SUGGESTION_PANEL_BASE_HTML_CLASS } from "./suggestion_panel_component";
import FileValidator from "../../../lib/uploader/file_validator";
import { transcodeImageToJpeg } from "../../../lib/uploader/image_transcoder";

// ContributionPanel — the first step: the photo(s) and the note. Edit and Add Photos are
// the SAME panel with two emphases (the title, the hint, the message label and the field
// order via CSS `order`); the focus is a modifier, not a second panel.
//
// It owns the file pipeline: a chosen photo is downscaled + transcoded to JPEG on a canvas
// BEFORE it is held (the server takes JPEG/WebP only — no HEIC, no PNG — and an iPhone photo
// is HEIC), previewed with an object URL, and removable. The transcoded files live on the
// host's state machine; the host POSTs them last, once identity is settled.
const FOCUS_COPY = {
  EDIT: {
    title: "Suggest an edit",
    hint: "Spotted something wrong or missing? Tell us — you can add photos too.",
    messageLabel: "Your suggestion",
  },
  PHOTO: {
    title: "Add photos",
    hint: "Share a photo of this place. Our crew review every photo before it goes on the site.",
    messageLabel: "Add a caption (optional)",
  },
};

export class ContributionPanel extends SuggestionPanelComponent {
  static get config() {
    return {
      componentTypeKey: "SuggestionContributionPanel",
      baseHtmlClass: SUGGESTION_PANEL_BASE_HTML_CLASS,
      selector: ".suggestion_panel__panel--contribution",
      phase: "contribution",

      titleSelector: ".suggestion_panel__title",
      hintSelector: ".suggestion_panel__hint",
      messageLabelSelector: ".suggestion_panel__message_label",
      messageInputSelector: "textarea[name='suggestion[message]']",
      fileInputSelector: "input[type=file]",
      previewsSelector: ".suggestion_panel__previews",
      progressBarSelector: ".suggestion_panel__progress__bar",
      continueSelector: ".suggestion_panel__continue",
      statusSelector: ".suggestion_panel__status",

      maxFiles: 10,
      maxFileSize: 20 * 1024 * 1024,
      // Anything the browser calls an image: it is transcoded to JPEG before it is posted,
      // so the server's narrower list (JPEG/WebP) is satisfied by the transcode, not the pick.
      allowedFileTypes: ["image/*"],
    };
  }

  constructor(id_or_element, host) {
    super(id_or_element, host, ContributionPanel.config);
    const config = ContributionPanel.config;

    this.titleEl = this.element.querySelector(config.titleSelector);
    this.hintEl = this.element.querySelector(config.hintSelector);
    this.messageLabelEl = this.element.querySelector(config.messageLabelSelector);
    this.messageInput = this.element.querySelector(config.messageInputSelector);
    this.fileInput = this.element.querySelector(config.fileInputSelector);
    this.previewsEl = this.element.querySelector(config.previewsSelector);
    this.progressBar = this.element.querySelector(config.progressBarSelector);
    this.statusEl = this.element.querySelector(config.statusSelector);

    this.validator = new FileValidator({
      maxFiles: config.maxFiles,
      maxFileSize: config.maxFileSize,
      allowedFileTypes: config.allowedFileTypes,
    });

    // Object URLs for the thumbnails leak until revoked, so they are tracked and cleared
    // whenever the previews repaint and again on destroy.
    this.previewUrls = [];
    this.onDestroy(() => this.revokePreviewUrls());

    // Continue → the identity step. The host validates "a message or a photo" (native
    // validation can't express it) before advancing.
    this.element.addEventListener("submit", (event) => {
      if (event) event.preventDefault();
      this.host.openIdentity();
    });

    if (this.fileInput) this.fileInput.addEventListener("change", this.handleFilesChosen.bind(this));
  }

  // --- Files -----------------------------------------------------------------

  async handleFilesChosen(event) {
    const chosen = Array.from((event && event.target && event.target.files) || []);
    if (!chosen.length) return;

    const existing = this.files();
    const { valid, validFiles, errors } = this.validator.validateFiles(chosen, existing);

    if (!valid) {
      this.dispatchAction("setStatus", { tone: "error", text: errors[0] });
      this.fileInput.value = "";
      return;
    }

    this.dispatchAction("setBusy", true);
    this.dispatchAction("setStatus", { tone: "info", text: "Preparing your photos…" });

    const prepared = [];
    for (const file of validFiles) {
      try {
        prepared.push(await transcodeImageToJpeg(file));
      } catch (error) {
        // One unreadable file shouldn't lose the others the person picked.
        this.dispatchAction("setStatus", { tone: "error", text: error.message });
      }
    }

    this.dispatchAction("setBusy", false);
    if (prepared.length) this.dispatchAction("setStatus", null);
    this.dispatchAction("setFiles", existing.concat(prepared));

    // Let the same photo be picked again after it has been removed.
    this.fileInput.value = "";
  }

  removeFileAt(index) {
    const files = this.files().slice();
    files.splice(index, 1);
    this.dispatchAction("setFiles", files);
  }

  files() {
    return this.readStateMachineValue("files") || [];
  }

  // --- Render ----------------------------------------------------------------

  render() {
    const isActive = this.renderPanelVisibility();

    this.renderCopy(this.readStateMachineValue("focus"));
    this.renderPreviews();
    this.renderProgress();
    this.renderContributionStatus();

    const busy = !!this.readStateMachineValue("busy");
    [this.messageInput, this.fileInput].forEach((input) => { if (input) input.disabled = busy; });

    return isActive;
  }

  renderCopy(focus) {
    const copy = FOCUS_COPY[focus] || FOCUS_COPY.EDIT;
    if (this.titleEl) this.titleEl.textContent = copy.title;
    if (this.hintEl) this.hintEl.textContent = copy.hint;
    if (this.messageLabelEl) this.messageLabelEl.textContent = copy.messageLabel;
  }

  renderPreviews() {
    if (!this.previewsEl) return;

    this.revokePreviewUrls();
    this.previewsEl.innerHTML = "";

    this.files().forEach((file, index) => {
      const objectUrl = URL.createObjectURL(file);
      this.previewUrls.push(objectUrl);

      const item = document.createElement("li");
      item.className = "suggestion_panel__preview";

      const image = document.createElement("img");
      image.className = "suggestion_panel__preview__image";
      image.src = objectUrl;
      image.alt = "";

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "suggestion_panel__preview__remove";
      remove.textContent = "Remove";
      remove.addEventListener("click", () => this.removeFileAt(index));

      item.appendChild(image);
      item.appendChild(remove);
      this.previewsEl.appendChild(item);
    });
  }

  revokePreviewUrls() {
    this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.previewUrls = [];
  }

  renderProgress() {
    if (!this.progressBar) return;
    const progress = this.readStateMachineValue("progress") || 0;
    this.progressBar.style.width = `${progress}%`;
  }

  // The contribution/submit status (file-prep, send errors) — distinct from the auth status
  // the auth panels paint via renderAuthStatus.
  renderContributionStatus() {
    if (!this.statusEl) return;
    const status = this.readStateMachineValue("status");
    this.statusEl.textContent = status ? status.text : "";
    ["info", "error"].forEach((tone) => this.statusEl.classList.remove(`suggestion_panel__status--${tone}`));
    if (status && status.tone) this.statusEl.classList.add(`suggestion_panel__status--${status.tone}`);
  }
}
